import { useState, useEffect, useMemo, useCallback } from "react";
import { BookOpen, Plus, Pencil, Trash2, Loader2, Search, X, Check, Download } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

// Fix DS (2026-09-22): "Contabilidad: libros contables, plan de cuentas, balance
// y cuadro de resultados" (Edson, 21/09). Primera etapa: el plan de cuentas
// editable. Mientras CEN no guarde ninguna cuenta se muestra un plan base, que
// se puede cargar de una vez con el botón y después ajustar.
const PLAN_EDIT_ROLES = ["Superadministrador", "Administrador", "Contador", "Gerencia Financiera"];

const TIPOS = [
  { v: "activo", l: "Activo" },
  { v: "pasivo", l: "Pasivo" },
  { v: "patrimonio", l: "Patrimonio" },
  { v: "ingreso", l: "Ingreso" },
  { v: "egreso", l: "Egreso" },
];

const TIPO_COLOR = {
  activo: "bg-blue-100 text-blue-700",
  pasivo: "bg-amber-100 text-amber-700",
  patrimonio: "bg-purple-100 text-purple-700",
  ingreso: "bg-emerald-100 text-emerald-700",
  egreso: "bg-rose-100 text-rose-700",
};

const VACIA = { codigo: "", nombre: "", tipo: "activo", imputable: true, activa: true };

export default function ContabilidadPlanCuentas() {
  const { user } = useAuth();
  const puedeEditar = PLAN_EDIT_ROLES.includes(user?.role);

  const [cuentas, setCuentas] = useState([]);
  const [esPlanBase, setEsPlanBase] = useState(false);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIA);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contabilidad/plan-cuentas`, { withCredentials: true });
      setCuentas(data?.cuentas || []);
      setEsPlanBase(!!data?.es_plan_base);
    } catch { toast.error("Error al cargar el plan de cuentas"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return cuentas.filter(c =>
      (!tipoFiltro || c.tipo === tipoFiltro) &&
      (!q || `${c.codigo} ${c.nombre}`.toLowerCase().includes(q))
    );
  }, [cuentas, busqueda, tipoFiltro]);

  const cargarBase = async () => {
    setGuardando(true);
    try {
      const { data } = await axios.post(`${API}/contabilidad/plan-cuentas/cargar-base`, {}, { withCredentials: true });
      toast.success(`Plan base cargado: ${data?.creadas || 0} cuentas`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar el plan base"); }
    finally { setGuardando(false); }
  };

  const abrirNueva = () => { setForm(VACIA); setEditando("nueva"); };
  const abrirEdicion = (c) => {
    setForm({ codigo: c.codigo, nombre: c.nombre, tipo: c.tipo, imputable: !!c.imputable, activa: c.activa !== false });
    setEditando(c._id);
  };

  const guardar = async () => {
    if (!form.codigo.trim() || !form.nombre.trim()) { toast.error("Completá el código y el nombre"); return; }
    setGuardando(true);
    try {
      if (editando === "nueva") {
        await axios.post(`${API}/contabilidad/plan-cuentas`, form, { withCredentials: true });
        toast.success("Cuenta creada");
      } else {
        await axios.put(`${API}/contabilidad/plan-cuentas/${editando}`, form, { withCredentials: true });
        toast.success("Cuenta actualizada");
      }
      setEditando(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar la cuenta"); }
    finally { setGuardando(false); }
  };

  const borrar = async (c) => {
    setGuardando(true);
    try {
      await axios.delete(`${API}/contabilidad/plan-cuentas/${c._id}`, { withCredentials: true });
      toast.success("Cuenta borrada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo borrar la cuenta"); }
    finally { setGuardando(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-h-full" data-testid="plan-cuentas-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <BookOpen size={26} className="text-[#2E6DA4]" /> Plan de Cuentas
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Las cuentas contables de la empresa, con su código, nombre y tipo</p>
        </div>
        {puedeEditar && (
          <div className="flex items-center gap-2">
            {esPlanBase && (
              <button onClick={cargarBase} disabled={guardando}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-[#2E6DA4] text-[#2E6DA4] hover:bg-[#2E6DA4]/5 disabled:opacity-50"
                data-testid="plan-cargar-base">
                {guardando ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Cargar plan base
              </button>
            )}
            <button onClick={abrirNueva}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#2E6DA4] text-white hover:bg-[#245A8A]"
              data-testid="plan-nueva-cuenta">
              <Plus size={14} /> Nueva cuenta
            </button>
          </div>
        )}
      </div>

      {esPlanBase && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800" data-testid="plan-aviso-base">
          Este es el <strong>plan base</strong> sugerido: todavía no hay ninguna cuenta guardada. Cargalo con el botón y después ajustá lo que haga falta.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por código o nombre"
            className="border border-gray-200 rounded-lg pl-8 pr-2 py-1.5 text-xs bg-white w-64" data-testid="plan-buscar" />
        </div>
        <select value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="plan-tipo-filtro">
          <option value="">Todos los tipos</option>
          {TIPOS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
        <span className="text-xs text-gray-400">{visibles.length} cuentas</span>
      </div>

      {editando && (
        <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm p-4 space-y-3" data-testid="plan-form">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-semibold text-gray-500">Código
              <input value={form.codigo} onChange={e => setForm(f => ({ ...f, codigo: e.target.value }))}
                className="block border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-32 mt-1" data-testid="plan-form-codigo" />
            </label>
            <label className="text-xs font-semibold text-gray-500 flex-1 min-w-[200px]">Nombre
              <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                className="block border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-full mt-1" data-testid="plan-form-nombre" />
            </label>
            <label className="text-xs font-semibold text-gray-500">Tipo
              <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                className="block border border-gray-200 rounded-lg px-2 py-1.5 text-sm mt-1" data-testid="plan-form-tipo">
                {TIPOS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
              <input type="checkbox" checked={form.imputable} onChange={e => setForm(f => ({ ...f, imputable: e.target.checked }))}
                data-testid="plan-form-imputable" /> Imputable
            </label>
            <button onClick={guardar} disabled={guardando}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#2E6DA4] text-white hover:bg-[#245A8A] disabled:opacity-50"
              data-testid="plan-form-guardar">
              {guardando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Guardar
            </button>
            <button onClick={() => setEditando(null)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
              data-testid="plan-form-cancelar">
              <X size={14} /> Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="plan-cuentas-tabla">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {["Código", "Nombre", "Tipo", "Imputable", ""].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400 text-sm">
                  <Loader2 size={20} className="mx-auto mb-2 animate-spin" /> Cargando…
                </td></tr>
              ) : visibles.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400 text-sm">Sin cuentas para mostrar</td></tr>
              ) : visibles.map((c, i) => (
                <tr key={c._id || `${c.codigo}-${i}`} className="border-b border-gray-50 hover:bg-gray-50/40"
                  data-testid={`plan-fila-${c.codigo}`}>
                  <td className="px-3 py-2.5 text-sm font-mono text-gray-700 whitespace-nowrap">{c.codigo}</td>
                  <td className="px-3 py-2.5 text-sm text-gray-900"
                    style={{ paddingLeft: `${12 + ((c.nivel || 1) - 1) * 16}px` }}>
                    <span className={c.imputable ? "" : "font-semibold"}>{c.nombre}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${TIPO_COLOR[c.tipo] || "bg-gray-100 text-gray-600"}`}>
                      {(TIPOS.find(t => t.v === c.tipo) || {}).l || c.tipo}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">{c.imputable ? "Sí" : "No"}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {puedeEditar && !esPlanBase && (
                      <>
                        <button onClick={() => abrirEdicion(c)} className="text-gray-400 hover:text-[#2E6DA4] mr-2"
                          data-testid={`plan-editar-${c.codigo}`}><Pencil size={14} /></button>
                        <button onClick={() => borrar(c)} className="text-gray-400 hover:text-rose-600"
                          data-testid={`plan-borrar-${c.codigo}`}><Trash2 size={14} /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
